<template>
	<div class="summary-module">
		<div id="summary-body">
			<div v-if="summaryModuleData.headline && summaryModuleData.description" class="summary-headline">
				<span class="header-strong" v-html="summaryModuleData.headline" />
				<span v-html="summaryModuleData.description" />
			</div>
			<div class="summary-data-container">
				<div class="data-points">
					<div>Price at NAV</div>
					<div class="strong" style="white-space: nowrap;">{{ summaryModuleData.priceAtNav }}</div>
					<div class="as-of-date">as of {{ summaryModuleData.navAsOfDate }} (updated daily)</div>
				</div>

				<div class="data-points">
					<div>Fund Assets (millions)</div>
					<div class="strong" style="white-space: nowrap;">{{ summaryModuleData.fundAssets }}</div>
				</div>

				<div class="data-points right-border">
					<sticky-text-wrap
						:text-content="formatManagerLabelText(summaryModuleData.managerCountTitle, 'superscript1')">
						<template #superscript1>
							<disclosure-superscript
								class="detail-page"
								anchor="portMgrName"
								:statistics="[getPortMgrName]"
							/>
						</template>
					</sticky-text-wrap>
					<div class="strong">{{ summaryModuleData.managerCount }}</div>
				</div>

				<div class="data-points">
					<sticky-text-wrap
						:text-content="formatManagerLabelText('Expense Ratio (Gross/Net %)', 'superscript2')">
						<template #superscript2>
							<disclosure-superscript class="detail-page" anchor="expenseRatio" :statistics="['expRatio']" />
						</template>
					</sticky-text-wrap>
					<div class="strong" style="white-space: nowrap;">
						{{ summaryModuleData.expenseRatioPercentGross }} / {{ summaryModuleData.expenseRatioPercentNet }}
					</div>
				</div>

				<div class="link-container">
					<div v-if="summaryModuleData.showFactSheet" class="links">
						<span v-if="summaryModuleData.quarterlyFundFactSheetURL">
							<a :href="`${summaryModuleData.quarterlyFundFactSheetURL}`" target="_blank">Quarterly Fund Fact Sheet</a>
							<span v-if="flagPDFURL(summaryModuleData.quarterlyFundFactSheetURL)">(PDF)</span>
						</span>
					</div>
				</div>

				<div class="link-container" v-if="summaryModuleData.prospectusURL">
					<div class="links">
						<span v-if="summaryModuleData.prospectusURL">
							<a :href="`${summaryModuleData.prospectusURL}`" target="_blank">Prospectus</a>
							<span v-if="flagPDFURL(summaryModuleData.prospectusURL)">(PDF)</span>
						</span>
					</div>
				</div>
			</div>
		</div>
	</div>
</template>

<script>
import { mapGetters } from 'vuex';

export default {
	name: 'SummaryModule',
	computed: {
		...mapGetters([
			'getFundAttributes',
			'getSummaryModuleData',
			'getReports'
		]),
		getPortMgrName() {
			const fundObjectiveToLower =
				(this.getFundAttributes &&
					this.getFundAttributes.Objective &&
					this.getFundAttributes.Objective.toLowerCase()) ||
				'';
			if (
				fundObjectiveToLower.includes('portfolioseries') ||
				fundObjectiveToLower.includes('managedrisk')
			) {
				return 'PSC_N';
			} else if (fundObjectiveToLower.includes('targetdate')) {
				return 'TDSC_N';
			}
			return 'portMgrName';
		},
		getManagerCountTitle() {
			const fundObjectiveToLower =
				(this.getFundAttributes &&
					this.getFundAttributes.Objective &&
					this.getFundAttributes.Objective.toLowerCase()) ||
				'';
			if (
				fundObjectiveToLower.includes('portfolioseries') ||
				fundObjectiveToLower.includes('managedrisk')
			) {
				return 'Portfolio Solutions Committee Members';
			} else if (fundObjectiveToLower.includes('targetdate')) {
				return 'Target Date Solutions Committee Members';
			}
			return 'Portfolio Managers';
		},
		prospectusURL() {
			let url = '';
			if (this.getReports && this.getReports.length > 0) {
				const reportData = this.getReports.find(val => val.Label === 'Prospectus');
				url = reportData && reportData.URL;
			}
			return url;
		},
		quarterlyFundFactSheetURL() {
			let url = '';
			if (this.getReports && this.getReports.length > 0) {
				const reportData = this.getReports.find(val => val.Label === 'Quarterly Fund Fact Sheet');
				url = reportData && reportData.URL;
			}
			return url;
		},
		summaryModuleData() {
			const data = Object.assign({}, this.getSummaryModuleData, {
				managerCountTitle: this.getManagerCountTitle,
				objective:
					(this.getFundAttributes &&
						this.getFundAttributes.Objective) ||
					'',
				prospectusURL: this.prospectusURL,
				quarterlyFundFactSheetURL: this.quarterlyFundFactSheetURL,
				showFactSheet:
					this.getAudience &&
					this.getAudience.toLowerCase() !== 'individual'
			});
			this.$EventBus.$emit('ExcelData', 'SummaryModule', data);
			return data;
		}
	},
	methods: {
		formatManagerLabelText(text, slotName, endText = '') {
			const first = text.split(' ');
			const last = first.pop();
			return [
				{
					content: [first.join(' '), ' '],
					noWrap: false
				},
				{
					content: [
						last,
						{ slotName },
						{ className: 'strong', text: endText }
					],
					noWrap: true
				}
			];
		},
		flagPDFURL(URL) {
			return URL.length && !URL.includes('https://capitalgroup.prospectus-express.com') && URL.toLowerCase().includes('pdf');
		}
	}
};
</script>

<style lang="less">
@import "~_css_/fonts.css";
@import '~_css_/variables';
.summary-module {
	#summary-body {
		border-top: 1px solid #cccccc;
		border-bottom: 1px solid #cccccc;
		font-family: 'AvenirNextLTW01-Regular', Arial;
		margin-bottom: 20px;

		.summary-headline {
		border-bottom: 1px solid #cccccc;
		font-size: 18px;
		line-height: 25px;
		padding: 10px 0;

		.header-strong {
			font-family: 'Avenir Next LT W01 Demi', Arial, sans-serif;
			font-size: 18px;
			color: #333333;
			padding-right: 5px;
		}
	}
	.summary-data-container {
		line-height: 18px;

		.as-of-date {
			font-family: 'AvenirNextLTW01-Condens 721299', Arial;
		}

		display: flex;
		font-size: 12px;
		color: #666666;

		.strong {
			font-size: 14px;
			color: #000000;
		}

		.data-points {
			color: @gray;
		}

		.data-points,
		.links {
			padding: 0 30px 0 0px;
			margin: 10px 0 10px 0;
			flex: 1 1 auto;
		}

		div:first-of-type {
			padding-left: 0px;
		}

		.link-container {
			color: #000000;
			font-size: 14px;
			display: inline-flex;

			// font-family: "AvenirNextLTW01-Regular", Arial;
			a {
				font-family: 'Avenir Next LT W01 Demi', Arial, sans-serif;
			}
		}
	}

		@media (max-width: 768px) {
			margin: 35px 0;
		}
	}

	#summary-bumper-description {
		padding-top: 10px;
		padding-bottom: 10px;
		border-bottom: 1px solid #d4d4d4;
	}

	.com-mod-outlier-point {
		font-size: 18px;
		font-family: "AvenirNextLTW01-Regular", Arial;
		line-height: 1.3em;
		color: #333;
	}

	.inline-item {
		color: #666;
		display: block;
		float: left;
		font-family: "AvenirNextLTW01-Regular", Arial;
		font-size: 12px;
		margin: 10px 26px 10px 0;
		width: 14%;

		.value {
			color: #333;
			font-family: "Avenir Next LT W01 Demi", Arial;
			font-size: 14px;
		}

		.after {
			font-family: "AvenirNextLTW01-Condens 721299", Arial;
		}

		&.link {
			font-size: 14px;
			color: #333;
		}

		a {
			color: #1d5799;
			font-family: "Avenir Next LT W01 Demi", Arial;
		}
	}

	.last-item {
		margin-right: 0;
	}

	.links {
		display: inline-block;
	}

	@media (max-width: 768px) {
		#summary-body {
			.summary-data-container {
				flex-wrap: wrap;
				line-height: 20px;

				.right-border {
					max-height: 20px;
					border-right-style: solid;
					border-right-width: 1px;
					border-right-color: #cccccc;
				}

				.link-container {
					border-top: 1px solid #cccccc;
					padding: 0 0 0 0;
					flex-basis: 100%;
				}
			}
			.reports {
				display: block;

				.clear {
					border-top: 1px solid #d4d4d4;
					clear: both;
				}

				.inline-item {
					width: 30%;
				}
			}
			.inline-item {
				width: 19%;
			}

			.separator {
				border-right: 1px solid lightgray;
			}
		}
	}

	@media (max-width: 480px) {
		#summary-body {
			.summary-data-container {
				.data-points {
					flex: 1 0 50%;
				}

				.right-border {
					padding-left: 0px;
					border-right: none;
				}
			}
			.separator {
				border: none;
			}

			.inline-item {
				width: 44%;
				margin-right: 0;
			}

			.left-col {
				margin-right: 26px;
			}

			.reports {
				.inline-item {
					width: 100%;
				}
			}
		}
	}
}


</style>
